import { useEffect, useState } from "react";
import { MdScience } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import { repositorioTalhao } from "../../sync/repositories/fazendas";
import { repositorioAnaliseSolo } from "../../sync/repositories/agricultura";

const AJUDA = {
  titulo: "Análise de Solo",
  descricao:
    "Cadastre aqui os valores brutos do laudo de laboratório — pH, Ca, Mg, K, Al, P, argila e micronutrientes. O sistema calcula a recomendação de calagem e adubação a partir desses valores; nenhum cálculo é digitado aqui, só o que o laboratório informou.",
  secoes: [
    {
      titulo: "Identificação",
      campos: [
        { nome: "Talhão", obrigatorio: true, explicacao: "Talhão de onde a amostra foi coletada." },
        { nome: "Camada analisada", obrigatorio: true, explicacao: "0–20 cm é a camada padrão. 20–40 cm é necessária para recomendar gessagem." },
        { nome: "Laboratório / Certificado", obrigatorio: false, explicacao: "Identificação do laudo, útil para conferência posterior." },
      ],
    },
    {
      titulo: "Valores do laudo",
      campos: [
        { nome: "K", obrigatorio: false, explicacao: "Digite em cmolc/dm³ (é o que o sistema usa pra calcular) — o mg/dm³ é calculado automaticamente no relatório." },
        { nome: "Campos em branco", obrigatorio: false, explicacao: "Deixe em branco o que o laudo não trouxe — o relatório mostra 'sem dados' em vez de inventar zero." },
      ],
    },
  ],
  dicas: [
    "A recomendação de gessagem só é calculada com a camada 20–40 cm cadastrada.",
    "Micronutrientes (S, Zn, Cu, B, Mn, Fe) são opcionais — muitos laudos não trazem essa análise.",
  ],
};

const CAMADA_OPTIONS = [
  { value: "0-20", label: "0–20 cm" },
  { value: "20-40", label: "20–40 cm" },
];

const FORM_VAZIO = {
  talhao: "", laboratorio: "", certificado: "", data_coleta: "", camada_cm: "0-20", cultura_finalidade: "",
  ph: "", ca: "", mg: "", k_cmolc: "", al: "", h_mais_al: "", p: "", materia_organica: "", argila_pct: "",
  s: "", zn: "", cu: "", b: "", mn: "", fe: "", observacoes: "",
};

const CAMPOS_QUIMICOS = [
  { name: "ph", label: "pH", passo: "0.1" },
  { name: "ca", label: "Ca (cmolc/dm³)", passo: "0.01" },
  { name: "mg", label: "Mg (cmolc/dm³)", passo: "0.01" },
  { name: "k_cmolc", label: "K (cmolc/dm³)", passo: "0.001" },
  { name: "al", label: "Al (cmolc/dm³)", passo: "0.01" },
  { name: "h_mais_al", label: "H+Al (cmolc/dm³)", passo: "0.01" },
  { name: "p", label: "P (mg/dm³)", passo: "0.01" },
  { name: "materia_organica", label: "Matéria orgânica (dag/kg)", passo: "0.01" },
  { name: "argila_pct", label: "Argila (%)", passo: "0.01" },
];

const CAMPOS_MICRO = [
  { name: "s", label: "S (mg/dm³)" },
  { name: "zn", label: "Zn (mg/dm³)" },
  { name: "cu", label: "Cu (mg/dm³)" },
  { name: "b", label: "B (mg/dm³)" },
  { name: "mn", label: "Mn (mg/dm³)" },
  { name: "fe", label: "Fe (mg/dm³)" },
];

export default function AmostraSolo() {
  const navigate = useNavigate();
  const [analises, setAnalises] = useState([]);
  const [talhoes, setTalhoes] = useState([]);
  const [carregandoLista, setCarregandoLista] = useState(true);
  const [loading, setLoading] = useState(false);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [editando, setEditando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [formData, setFormData] = useState(FORM_VAZIO);

  useEffect(() => {
    carregarAnalises();
    carregarTalhoes();
  }, []);

  const carregarAnalises = async () => {
    try {
      setCarregandoLista(true);
      const response = await repositorioAnaliseSolo.listar();
      setAnalises(response.data.results || response.data);
    } catch (error) {
      console.error("Erro ao carregar análises de solo:", error);
    } finally {
      setCarregandoLista(false);
    }
  };

  const carregarTalhoes = async () => {
    try {
      const response = await repositorioTalhao.listar();
      setTalhoes(response.data.results || response.data);
    } catch (error) {
      console.error("Erro ao carregar talhões:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNovaAnalise = () => {
    setFormData(FORM_VAZIO);
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(true);
  };

  const handleEditar = (analise) => {
    const preenchido = { ...FORM_VAZIO };
    Object.keys(preenchido).forEach((campo) => {
      if (campo === "talhao") {
        preenchido.talhao = analise.talhao ?? "";
      } else if (analise[campo] !== null && analise[campo] !== undefined) {
        preenchido[campo] = analise[campo];
      }
    });
    setFormData(preenchido);
    setEditando(true);
    setEditandoId(analise.id);
    setMostrarFormulario(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancel = () => {
    setFormData(FORM_VAZIO);
    setEditando(false);
    setEditandoId(null);
    setMostrarFormulario(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.talhao) {
      alert("Selecione o talhão.");
      return;
    }

    // Campos vazios viram null (nunca 0) — laudo que não trouxe o valor
    // não pode virar "zero" pro motor de recomendação.
    const payload = {};
    Object.entries(formData).forEach(([campo, valor]) => {
      payload[campo] = valor === "" ? null : valor;
    });

    setLoading(true);
    try {
      if (editando) {
        await repositorioAnaliseSolo.atualizar(editandoId, payload);
      } else {
        await repositorioAnaliseSolo.criar(payload);
      }
      handleCancel();
      carregarAnalises();
    } catch (error) {
      console.error("Erro ao salvar análise de solo:", error);
      const data = error.response?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const erros = Object.values(data).flat();
        alert(erros.join("\n"));
      } else if (Array.isArray(data)) {
        alert(data.join("\n"));
      } else {
        alert(error.message || "Erro ao salvar. Verifique os dados e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExcluir = async (id) => {
    if (!window.confirm("Excluir esta análise de solo? Esta ação não pode ser desfeita.")) return;
    try {
      await repositorioAnaliseSolo.excluir(id);
      carregarAnalises();
    } catch (error) {
      console.error("Erro ao excluir:", error);
      alert(error.message || "Erro ao excluir análise de solo.");
    }
  };

  const nomeTalhao = (id) => talhoes.find((t) => String(t.id) === String(id))?.nome_codigo || "—";

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-2">
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              Análise de Solo <MdScience size={32} />
            </h2>
            <AjudaTela {...AJUDA} />
          </div>
          {!mostrarFormulario && (
            <button
              onClick={handleNovaAnalise}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors shadow-md"
            >
              + Nova Análise
            </button>
          )}
        </div>

        {mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">
              {editando ? "Editar Análise de Solo" : "Cadastrar Análise de Solo"}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Identificação</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Talhão *</label>
                    <select
                      name="talhao"
                      value={formData.talhao}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                      required
                    >
                      <option value="">Selecione...</option>
                      {talhoes.map((t) => (
                        <option key={t.id} value={t.id}>{t.nome_codigo}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Camada analisada *</label>
                    <select
                      name="camada_cm"
                      value={formData.camada_cm}
                      onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    >
                      {CAMADA_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Data da coleta</label>
                    <input
                      type="date" name="data_coleta" value={formData.data_coleta} onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Laboratório</label>
                    <input
                      type="text" name="laboratorio" value={formData.laboratorio} onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Nº do certificado</label>
                    <input
                      type="text" name="certificado" value={formData.certificado} onChange={handleChange}
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">Cultura / finalidade</label>
                    <input
                      type="text" name="cultura_finalidade" value={formData.cultura_finalidade} onChange={handleChange}
                      placeholder="Ex.: Pastagem — formação"
                      className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                    />
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Valores químicos do laudo</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {CAMPOS_QUIMICOS.map((c) => (
                    <div key={c.name} className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">{c.label}</label>
                      <input
                        type="number" step={c.passo} name={c.name} value={formData[c.name]} onChange={handleChange}
                        className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
                        placeholder="—"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Micronutrientes (opcional)</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {CAMPOS_MICRO.map((c) => (
                    <div key={c.name} className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">{c.label}</label>
                      <input
                        type="number" step="0.01" name={c.name} value={formData[c.name]} onChange={handleChange}
                        className="border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300"
                        placeholder="—"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">Observações</label>
                <textarea
                  name="observacoes" value={formData.observacoes} onChange={handleChange}
                  className="border border-green-500 p-2 rounded h-20 focus:ring-2 focus:ring-green-300"
                />
              </div>

              <div className="flex gap-4 pt-2">
                <button
                  type="submit" disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:bg-blue-300"
                >
                  {loading ? "Salvando..." : editando ? "Atualizar" : "Cadastrar"}
                </button>
                <button
                  type="button" onClick={handleCancel}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-2 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        {!mostrarFormulario && (
          <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <p className="text-gray-600">{analises.length} análise(s) cadastrada(s)</p>
            </div>
            {carregandoLista ? (
              <div className="p-8 text-center text-gray-500">Carregando...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Talhão</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Camada</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Data da coleta</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Cultura/finalidade</th>
                      <th className="p-3 text-left text-sm font-semibold text-gray-700">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analises.map((a) => (
                      <tr key={a.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                        <td className="p-3 text-sm font-medium">{a.talhao_nome || nomeTalhao(a.talhao)}</td>
                        <td className="p-3 text-sm">{a.camada_cm === "20-40" ? "20–40 cm" : "0–20 cm"}</td>
                        <td className="p-3 text-sm">{a.data_coleta || "—"}</td>
                        <td className="p-3 text-sm">{a.cultura_finalidade || "—"}</td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => navigate(`/relatorios/agricolas/analise-solo/${a.id}`)}
                              className="bg-brand-green hover:bg-emerald-900 text-white px-3 py-1 rounded text-sm transition-colors"
                            >
                              Ver relatório
                            </button>
                            <button
                              onClick={() => handleEditar(a)}
                              className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-sm transition-colors"
                            >
                              Editar
                            </button>
                            <button
                              onClick={() => handleExcluir(a.id)}
                              className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
                            >
                              Excluir
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {analises.length === 0 && (
                      <tr>
                        <td colSpan="5" className="p-8 text-center text-gray-500">Nenhuma análise de solo cadastrada.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
